import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { useI18n } from "../context/I18nContext";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ShieldCheck, EnvelopeSimple, WhatsappLogo, SignOut, UserGear } from "@phosphor-icons/react";

export const SecuritySettings = ({ open, onClose }) => {
  const { t } = useI18n();
  const { user, api, logout, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [channel, setChannel] = useState(user?.twofa_channel || "email");
  const [step, setStep] = useState("idle");
  const [code, setCode] = useState("");
  const [hint, setHint] = useState("");
  const [busy, setBusy] = useState(false);
  const isAdmin = user?.role === "admin";
  const [policyOn, setPolicyOn] = useState(true);
  const [cred, setCred] = useState({ current_password: "", new_email: "", new_password: "" });
  const [credBusy, setCredBusy] = useState(false);
  React.useEffect(() => {
    if (open) setCred({ current_password: "", new_email: user?.email || "", new_password: "" });
  }, [open]); // eslint-disable-line
  React.useEffect(() => {
    if (open && isAdmin) {
      api.get("/admin/security-settings").then((r) => setPolicyOn(r.data.require_admin_2fa)).catch(() => {});
    }
  }, [open, isAdmin]); // eslint-disable-line
  const savePolicy = async (val) => {
    setPolicyOn(val);
    try { await api.put("/admin/security-settings", { require_admin_2fa: val }); toast.success(t("sec_policy_saved")); }
    catch (e) { toast.error("Error"); }
  };

  const start = async () => {
    setBusy(true);
    try {
      const r = await api.post("/me/2fa/start", { channel });
      setHint(r.data.hint); setStep("code");
      toast.success(t("sec_code_sent"));
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setBusy(false); }
  };
  const confirm = async () => {
    setBusy(true);
    try {
      await api.post("/me/2fa/confirm", { channel, code: code.trim() });
      await refreshUser();
      setStep("idle"); setCode("");
      toast.success(t("sec_enabled_ok"));
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setBusy(false); }
  };
  const disable = async () => {
    setBusy(true);
    try { await api.post("/me/2fa/disable"); await refreshUser(); toast.success(t("sec_disabled_ok")); }
    catch (e) { toast.error("Error"); } finally { setBusy(false); }
  };
  const logoutAll = async () => {
    setBusy(true);
    try { await api.post("/me/logout-all"); toast.success(t("sec_logout_all_done")); logout(); navigate("/login"); }
    catch (e) { toast.error("Error"); } finally { setBusy(false); }
  };

  const saveCred = async () => {
    if (!cred.current_password) { toast.error(t("cred_current_pw_required")); return; }
    setCredBusy(true);
    try {
      const payload = { current_password: cred.current_password };
      if (cred.new_email && cred.new_email.trim().toLowerCase() !== (user?.email || "").toLowerCase()) payload.new_email = cred.new_email.trim();
      if (cred.new_password) payload.new_password = cred.new_password;
      const { data } = await api.put("/me/credentials", payload);
      if (data.token) localStorage.setItem("coop_token", data.token);
      await refreshUser();
      setCred({ current_password: "", new_email: cred.new_email, new_password: "" });
      toast.success(t("cred_saved_ok"));
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setCredBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) { setStep("idle"); setCode(""); onClose(); } }}>
      <DialogContent className="max-w-md" data-testid="security-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-emerald-900"><ShieldCheck size={22} weight="fill" /> {t("sec_title")}</DialogTitle>
        </DialogHeader>
        <div className="space-y-5">
          <div className="rounded-xl border border-emerald-100 p-4">
            <div className="flex items-center justify-between gap-2">
              <div>
                <p className="font-semibold text-emerald-900">{t("sec_2fa")}</p>
                <p className="text-xs text-slate-500 mt-0.5">{t("sec_2fa_desc")}</p>
              </div>
              <span data-testid="sec-2fa-state" className={`text-xs font-bold px-2 py-1 rounded-full ${user?.twofa_enabled ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{user?.twofa_enabled ? t("sec_2fa_on") : t("sec_2fa_off")}</span>
            </div>
            {user?.twofa_enabled ? (
              <Button data-testid="sec-2fa-disable" onClick={disable} disabled={busy} variant="outline" className="mt-3 w-full border-red-200 text-red-700 hover:bg-red-50">{t("sec_disable")}</Button>
            ) : step === "idle" ? (
              <div className="mt-3 space-y-2">
                <div className="flex gap-2">
                  <button type="button" data-testid="sec-ch-email" onClick={() => setChannel("email")} className={`flex-1 flex items-center justify-center gap-1.5 text-sm font-semibold py-2 rounded-lg border transition-colors ${channel === "email" ? "border-emerald-600 bg-emerald-50 text-emerald-800" : "border-slate-200 text-slate-500"}`}><EnvelopeSimple size={16} weight="bold" /> {t("sec_channel_email")}</button>
                  <button type="button" data-testid="sec-ch-wa" onClick={() => setChannel("whatsapp")} className={`flex-1 flex items-center justify-center gap-1.5 text-sm font-semibold py-2 rounded-lg border transition-colors ${channel === "whatsapp" ? "border-emerald-600 bg-emerald-50 text-emerald-800" : "border-slate-200 text-slate-500"}`}><WhatsappLogo size={16} weight="bold" /> {t("sec_channel_wa")}</button>
                </div>
                <Button data-testid="sec-2fa-enable" onClick={start} disabled={busy} className="w-full bg-emerald-800 hover:bg-emerald-900">{t("sec_enable")}</Button>
              </div>
            ) : (
              <div className="mt-3 space-y-2">
                <p className="text-xs text-slate-500">{t("sec_verify_code")} <b className="text-emerald-800">{hint}</b></p>
                <Input data-testid="sec-2fa-code" value={code} onChange={(e) => setCode(e.target.value)} inputMode="numeric" maxLength={6} placeholder="000000" className="text-center tracking-[0.4em] text-lg" />
                <Button data-testid="sec-2fa-confirm" onClick={confirm} disabled={busy || code.length < 6} className="w-full bg-emerald-800 hover:bg-emerald-900">{t("sec_confirm")}</Button>
              </div>
            )}
          </div>
          {isAdmin && (
          <div className="rounded-xl border border-emerald-100 p-4" data-testid="sec-admin-policy">
            <p className="text-xs text-emerald-800 bg-emerald-50 rounded-md px-3 py-2 mb-3">{t("sec_admin_required")}</p>
            <div className="flex items-center justify-between gap-2">
              <div>
                <p className="font-semibold text-emerald-900">{t("sec_policy")}</p>
                <p className="text-xs text-slate-500 mt-0.5">{t("sec_policy_desc")}</p>
              </div>
              <button type="button" data-testid="sec-policy-toggle" onClick={() => savePolicy(!policyOn)} className={`shrink-0 w-12 h-7 rounded-full transition-colors relative ${policyOn ? "bg-emerald-600" : "bg-slate-300"}`}>
                <span className={`absolute top-0.5 h-6 w-6 bg-white rounded-full shadow transition-all ${policyOn ? "left-[22px]" : "left-0.5"}`} />
              </button>
            </div>
          </div>
          )}
          <div className="rounded-xl border border-emerald-100 p-4" data-testid="cred-section">
            <div className="flex items-center gap-2 mb-1">
              <UserGear size={18} weight="fill" className="text-emerald-700" />
              <p className="font-semibold text-emerald-900">{t("cred_title")}</p>
            </div>
            <p className="text-xs text-slate-500 mb-3">{t("cred_desc")}</p>
            <div className="space-y-2">
              <div>
                <label className="text-xs font-medium text-slate-600">{t("cred_new_email")}</label>
                <Input data-testid="cred-new-email" type="email" value={cred.new_email} onChange={(e) => setCred({ ...cred, new_email: e.target.value })} className="mt-1" />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600">{t("cred_new_pw")}</label>
                <Input data-testid="cred-new-password" type="password" value={cred.new_password} onChange={(e) => setCred({ ...cred, new_password: e.target.value })} placeholder="••••••" className="mt-1" />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600">{t("cred_current_pw")}</label>
                <Input data-testid="cred-current-password" type="password" value={cred.current_password} onChange={(e) => setCred({ ...cred, current_password: e.target.value })} placeholder="••••••" className="mt-1" />
              </div>
              <Button data-testid="cred-save-btn" onClick={saveCred} disabled={credBusy || !cred.current_password} className="w-full bg-emerald-800 hover:bg-emerald-900">{credBusy ? t("loading") : t("cred_save")}</Button>
            </div>
          </div>
          <div className="rounded-xl border border-slate-100 p-4">
            <p className="font-semibold text-emerald-900">{t("sec_logout_all")}</p>
            <p className="text-xs text-slate-500 mt-0.5">{t("sec_logout_all_desc")}</p>
            <Button data-testid="sec-logout-all" onClick={logoutAll} disabled={busy} variant="outline" className="mt-3 w-full gap-1.5"><SignOut size={16} weight="bold" /> {t("sec_logout_all")}</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
